import { Middleware, MiddlewareAPI, isRejectedWithValue } from '@reduxjs/toolkit';
import type { AnyAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8901/api';

// List of endpoints that should NOT trigger token refresh
const AUTH_ENDPOINTS = [
  '/auth/signin',
  '/auth/register',
  '/auth/signup',
  '/auth/signin',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-otp',
];

/**
 * Check if endpoint should skip token refresh logic
 */
const shouldSkipRefresh = (action: AnyAction): boolean => {
  // Check endpoint name from RTK Query action
  const endpointName = action?.meta?.arg?.endpointName as string | undefined;
  if (endpointName) {
    return AUTH_ENDPOINTS.some(authEndpoint => endpointName.toLowerCase().includes(authEndpoint.toLowerCase()));
  }

  // Check URL from action type
  const actionType = action?.type as string;
  if (actionType) {
    return AUTH_ENDPOINTS.some(authEndpoint => actionType.toLowerCase().includes(authEndpoint.toLowerCase()));
  }

  return false;
};

/**
 * Token Refresh Middleware
 * Automatically refreshes expired tokens (but not during login/signup)
 */
export const tokenRefreshMiddleware: Middleware = (storeAPI: MiddlewareAPI) => (next) => async (action: AnyAction) => {
  // Pass action through first
  const result = next(action);

  // Check if this is an API rejection with 401 status
  if (isRejectedWithValue(action)) {
    const payload = action.payload as any;
    
    // Skip refresh for auth endpoints (login, signup, etc.)
    if (shouldSkipRefresh(action)) {
      console.log('⏭️  Skipping token refresh for auth endpoint');
      return result;
    }

    // Check for 401 Unauthorized
    if (payload?.status === 401 || payload?.originalStatus === 401) {
      console.log('🔄 Token expired, attempting refresh...');

      const state: any = storeAPI.getState();
      const refreshToken = state.auth?.refreshToken;
      const token = state.auth?.token;

      // Only attempt refresh if we have both tokens (user is logged in)
      if (refreshToken && token) {
        try {
          // Prevent multiple simultaneous refresh attempts
          if ((window as any).__refreshingToken) {
            console.log('⏳ Token refresh already in progress...');
            return result;
          }

          (window as any).__refreshingToken = true;

          // Call refresh endpoint
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          if (response.data?.jwt) {
            console.log('✅ Token refreshed successfully');
            
            // Update tokens in Redux
            storeAPI.dispatch({
              type: 'auth/setTokens',
              payload: {
                token: response.data.jwt,
                refreshToken: response.data.refresh_token || refreshToken,
              },
            });

            (window as any).__refreshingToken = false;
            // RTK Query will automatically retry the failed request
          } else {
            throw new Error('No access token in response');
          }
        } catch (error: any) {
          console.error('❌ Token refresh failed:', error);
          (window as any).__refreshingToken = false;
          
          // Only logout if refresh actually failed (not if it's a network error)
          if (error?.response?.status === 401 || error?.response?.status === 403) {
            console.log('🚪 Logging out user due to failed token refresh');
            
            // Logout user
            storeAPI.dispatch({ type: 'auth/logout' });
            
            // Redirect to login only if not already there
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/signin') && !window.location.pathname.includes('/signin')) {
              window.location.href = '/signin';
            }
          } else {
            console.warn('⚠️  Network error during token refresh, not logging out');
          }
        }
      } else {
        // No refresh token available - user is not authenticated
        // But don't redirect if we're already on an auth page or it's a login attempt
        if (!shouldSkipRefresh(action)) {
          console.warn('⚠️  No tokens available, user not authenticated');
          storeAPI.dispatch({ type: 'auth/logout' });
          
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/signin') && !window.location.pathname.includes('/signin')) {
            window.location.href = '/signin';
          }
        }
      }
    }
  }

  return result;
};
